import React, { useEffect, useState } from "react";
import { fetchBlogs } from "../services/api";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import "../index.css";
import { Link } from "react-router-dom";
import { formatUrl } from "../utils/config";

// ---- Placeholder component for broken/missing images ----
const ImagePlaceholder = ({ icon = "fa-newspaper", height = "auto" }) => (
  <div
    style={{
      width: "100%",
      height: height,
      background: "linear-gradient(135deg, #f0f0f0 0%, #e0e0e0 100%)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: "8px",
      minHeight: "200px",
    }}
  >
    <i
      className={`fal ${icon}`}
      style={{ fontSize: "3rem", color: "#bbb" }}
    ></i>
  </div>
);

const BlogArea = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Track broken images per blog (by index or id)
  const [brokenImages, setBrokenImages] = useState({});

  const SkeletonLoader = () => (
    <div className="col-xl-6 mb-30 mb-xl-0">
      <div className="blog-grid style-big">
        <div className="blog-img">
          <Skeleton height={400} />
        </div>
        <div className="blog-content">
          <div className="blog-meta">
            <Skeleton width={100} style={{ marginRight: "10px" }} />
            <Skeleton width={80} />
          </div>
          <h3 className="blog-title">
            <Skeleton width={300} />
          </h3>
          <Skeleton count={3} />
          <Skeleton width={120} height={35} />
        </div>
      </div>
    </div>
  );

  const SmallBlogSkeleton = () => (
    <div className="blog-grid style2 style-small mb-30">
      <div className="blog-img">
        <Skeleton height={250} width={200} />
      </div>
      <div className="blog-content">
        <div className="blog-meta">
          <Skeleton width={80} style={{ marginRight: "10px" }} />
          <Skeleton width={60} />
        </div>
        <h3 className="blog-title">
          <Skeleton width={200} />
        </h3>
        <Skeleton count={2} />
        <Skeleton width={100} height={30} />
      </div>
    </div>
  );

  useEffect(() => {
    const loadBlogs = async () => {
      try {
        const data = await fetchBlogs();
        if (!data || !data.payload?.page?.length) {
          setError("No blogs found.");
        } else {
          setBlogs(data.payload.page.slice(0, 3));
        }
      } catch (err) {
        console.error("Error fetching blogs:", err);
      } finally {
        setLoading(false);
      }
    };

    loadBlogs();
  }, []);

  // Handler for image load error
  const handleImageError = (id) => {
    setBrokenImages((prev) => ({ ...prev, [id]: true }));
  };

  return (
    <>
      {!loading && !error && blogs.length === 0 ? (
        <p className="text-warning text-center py-5">No blog posts available at the moment.</p>
      ) : (
        <section className="space" id="blog-sec">
          <div className="container">
            <div className="row text-center text-lg-start justify-content-lg-between align-items-end">
              <div className="col-lg-6 mb-n2 mb-lg-0">
                <div className="title-area">
                  <span className="sub-title6 justify-content-lg-start justify-content-center">
                    <span className="shape left d-lg-none">
                      <span className="dots"></span>
                    </span>
                    BLOGS
                    <span className="shape right">
                      <span className="dots"></span>
                    </span>
                  </span>
                  <h2 className="sec-title">Latest News & Blog</h2>
                </div>
              </div>
              <div className="col-lg-auto">
                <div className="sec-btn">
                  <Link to="/blog" className="th-btn style-new">
                    VIEW ALL POSTS <i className="fas fa-arrow-right ms-2"></i>
                  </Link>
                </div>
              </div>
            </div>

            {error && <p className="text-danger text-center">{error}</p>}

            <div className="row">
              {loading ? (
                <>
                  <SkeletonLoader />
                  <div className="col-xl-6">
                    {[1, 2, 3].map((_, index) => (
                      <SmallBlogSkeleton key={index} />
                    ))}
                  </div>
                </>
              ) : (
                blogs.length > 0 && (
                  <>
                    {/* Main Blog */}
                    <div className="col-xl-6 mb-30 mb-xl-0">
                      <div className="blog-grid style-big">
                        <div className="blog-img">
                          {brokenImages[blogs[0].id] || !blogs[0].thumbnail ? (
                            <ImagePlaceholder icon="fa-newspaper" height="400px" />
                          ) : (
                            <img
                              src={formatUrl(blogs[0].thumbnail)}
                              alt="blog"
                              className="img-fluid"
                              onError={() => handleImageError(blogs[0].id)}
                            />
                          )}
                        </div>
                        <div className="blog-content">
                          <div className="blog-meta">
                            <Link to="blog">
                              <i className="fal fa-calendar-days"></i>
                              {new Date(blogs[0].createdAt).toLocaleDateString() || "Date not available"}
                            </Link>
                          </div>
                          <h3 className="blog-title">
                            <Link to={`blogdetails?id=${blogs[0].id}`}>
                              {blogs[0].title}
                            </Link>
                          </h3>
                          <p className="blog-text">
                            {blogs[0].content?.substring(0, 150)}...
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Smaller Blogs */}
                    <div className="col-xl-6">
                      {blogs.slice(1, 4).map((blog) => (
                        <div key={blog.id} className="blog-grid style2 style-small mb-30">
                          <div className="blog-img">
                            {brokenImages[blog.id] || !blog.thumbnail ? (
                              <ImagePlaceholder icon="fa-newspaper" height="250px" />
                            ) : (
                              <img
                                src={formatUrl(blog.thumbnail)}
                                alt="blog"
                                className="img-fluid"
                                style={{ width: "100%", height: "250px", objectFit: "cover" }}
                                onError={() => handleImageError(blog.id)}
                              />
                            )}
                          </div>
                          <div className="blog-content">
                            <div className="blog-meta">
                              <Link to="blog">
                                <i className="fal fa-calendar-days"></i>
                                {new Date(blog.createdAt).toLocaleDateString() || "Date not available"}
                              </Link>
                            </div>
                            <h3 className="blog-title">
                              <Link to={`/blogdetails/${blog.id}`} state={{ blogData: blog }}>
                                {blog.title}
                              </Link>
                            </h3>
                            <p className="blog-text">
                              {blog.content?.substring(10, 100)}...
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )
              )}
            </div>
          </div>
        </section>
      )}
    </>
  );
};

export default BlogArea;